import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Paperclip, Send, Users } from "lucide-react";
import { messagesApi } from "@/api/messaging";
import { isSessionExpired } from "@/api/polling";
import type {
  AttachmentRef,
  ConversationThreadDto,
  MessageAttachmentDto,
  MessageDto,
  UserPickerDto,
} from "@/api/messaging";
import { messagingKeys } from "./messagingKeys";
import { Button } from "@/design-system/components/ui";
import { ResourceChip } from "@/components/messaging/ResourceChip";
import { AttachResourceModal } from "@/components/messaging/AttachResourceModal";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { ApiError } from "@/api/client";
import { cn } from "@/design-system/lib/utils";
import {
  formatDateSeparator,
  formatFullLocalDateTime,
  formatMessageTime,
} from "@/components/messaging/messageTime";
import { useUserTimeZone } from "@/utils/datetime";

/**
 * Internal Messaging v1.0 §6 — single-thread view. Combines two routes:
 *   /messages/:id          — load + render an existing conversation.
 *   /messages/new?to={id}  — empty thread; first send creates the row.
 *
 * Polling: every 7s while document.visibilityState === "visible". Pauses on
 * tab hide; resumes on focus. Single useEffect, no library — spec §6.
 */
export function ThreadPage() {
  const params = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const isNew = !params.id;
  const recipientHint = searchParams.get("to") ?? null;

  if (isNew && recipientHint) {
    return <NewThread recipientUserId={recipientHint} />;
  }
  if (!params.id) {
    // /messages/new without ?to= is meaningless — bounce back to inbox.
    return <BackToInbox />;
  }
  return <ExistingThread conversationId={params.id} />;
}

function BackToInbox() {
  const navigate = useNavigate();
  useEffect(() => { navigate("/messages", { replace: true }); }, [navigate]);
  return null;
}

function ExistingThread({ conversationId }: { conversationId: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const thread = useQuery({
    queryKey: ["messaging", "thread", conversationId],
    queryFn: () => messagesApi.getConversation(conversationId),
    refetchOnWindowFocus: true,
  });

  // Local message buffer — seeded from the thread, appended by polling +
  // by the optimistic send. Held in component state so we can append without
  // re-fetching the entire thread on every poll.
  const [messages, setMessages] = useState<MessageDto[]>([]);
  useEffect(() => {
    if (thread.data) setMessages(thread.data.messages);
  }, [thread.data]);

  // Refresh the unread badge as soon as the server marks messages read.
  useEffect(() => {
    if (thread.data) {
      queryClient.invalidateQueries({ queryKey: ["messaging", "unread"] });
      queryClient.invalidateQueries({ queryKey: ["messaging", "conversations"] });
    }
  }, [thread.data, queryClient]);

  // Polling loop — visibility-aware.
  useEffect(() => {
    if (!thread.data) return;
    let cancelled = false;
    let timer: number | null = null;

    const tick = async () => {
      if (document.visibilityState !== "visible" || cancelled) return;
      const last = lastSeenIdRef.current;
      try {
        const fresh = await messagesApi.poll(conversationId, last ?? undefined);
        if (cancelled || fresh.length === 0) return;
        setMessages((prev) => mergeMessages(prev, fresh));
        queryClient.invalidateQueries({ queryKey: ["messaging", "unread"] });
        queryClient.invalidateQueries({ queryKey: ["messaging", "conversations"] });
      } catch (err) {
        // Session expired — stop the loop so an unattended tab doesn't 401
        // every 7s indefinitely (the user lands on login on next navigation).
        if (isSessionExpired(err)) {
          cancelled = true;
          stop();
          return;
        }
        // 404 means the conversation is gone — bounce.
      }
    };

    const start = () => {
      stop();
      timer = window.setInterval(() => { void tick(); }, 7000);
    };
    const stop = () => {
      if (timer !== null) window.clearInterval(timer);
      timer = null;
    };
    const onVis = () => {
      if (document.visibilityState === "visible") {
        void tick();
        start();
      } else {
        stop();
      }
    };

    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      stop();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [conversationId, thread.data, queryClient]);

  const lastSeenIdRef = useRef<string | null>(null);
  useEffect(() => {
    lastSeenIdRef.current = messages.length > 0 ? messages[messages.length - 1].id : null;
  }, [messages]);

  // Show the other participant's name in the breadcrumb instead of the raw
  // conversation id.
  useBreadcrumbs(
    thread.data
      ? [
          { label: "Messages", href: "/messages" },
          { label: thread.data.otherUserDisplayName },
        ]
      : null,
  );

  if (thread.isLoading) {
    return <div className="text-small text-gray-mid">Loading conversation…</div>;
  }
  if (thread.isError || !thread.data) {
    return (
      <div className="text-small text-gray-mid flex flex-col gap-space-2">
        <div>Conversation not found.</div>
        <Button onClick={() => navigate("/messages")}>Back to inbox</Button>
      </div>
    );
  }

  return (
    <ThreadView
      header={<ThreadHeader thread={thread.data} />}
      messages={messages}
      conversationId={conversationId}
      recipientUserId={thread.data.otherUserId}
      canSend={thread.data.viewerIsParticipant}
      onMessageSent={(msg) => setMessages((prev) => mergeMessages(prev, [msg]))}
    />
  );
}

function NewThread({ recipientUserId }: { recipientUserId: string }) {
  // Fetch the recipient's display name so the header has something to show
  // before the conversation exists.
  const recipient = useQuery({
    queryKey: ["messaging", "user-picker", recipientUserId],
    queryFn: () => messagesApi.searchUsers(""),
  });
  const match: UserPickerDto | undefined = recipient.data?.find((u) => u.id === recipientUserId);

  // Internal Messaging Integration Standard v1.1 §5 — when this thread was
  // opened from an entity Messages tab's compose affordance, the host page
  // forwards an optimistic chip via React Router state. The empty thread
  // seeds <c>pendingAttachments</c> with it so the user lands on a draft
  // already linked to the record.
  const location = useLocation();
  const state = location.state as { prefillAttachment?: MessageAttachmentDto } | null;
  const prefillAttachment = state?.prefillAttachment;

  return (
    <ThreadView
      header={
        <NewThreadHeader displayName={match?.displayName ?? recipientUserId} />
      }
      messages={[]}
      conversationId={null}
      recipientUserId={recipientUserId}
      canSend
      initialAttachments={prefillAttachment ? [prefillAttachment] : undefined}
    />
  );
}

interface ThreadViewProps {
  header: React.ReactNode;
  messages: MessageDto[];
  conversationId: string | null;
  recipientUserId: string;
  canSend: boolean;
  onMessageSent?: (msg: MessageDto) => void;
  initialAttachments?: MessageAttachmentDto[];
}

function ThreadView({
  header, messages, conversationId, recipientUserId, canSend, onMessageSent,
  initialAttachments,
}: ThreadViewProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user: me } = useAuth();
  const tz = useUserTimeZone();
  const [body, setBody] = useState("");
  const [pendingAttachments, setPendingAttachments] = useState<MessageAttachmentDto[]>(
    initialAttachments ?? [],
  );
  const [attachOpen, setAttachOpen] = useState(false);
  // FIND-06 — context guard. Non-null while we're warning the user that the
  // recipient's existing thread is about a different record.
  const [foreignContexts, setForeignContexts] = useState<MessageAttachmentDto[] | null>(null);
  const [checkingContext, setCheckingContext] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef(true);

  const groupedByDay = useMemo(() => groupMessagesByDay(messages, tz), [messages, tz]);

  // Auto-scroll to bottom on first load AND when a new message arrives while
  // we're already at the bottom. Don't yank scroll if the user has scrolled
  // up to read history.
  useEffect(() => {
    if (!scrollRef.current) return;
    if (stickToBottomRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const el = scrollRef.current;
    // 32px slack — counts "near the bottom" as still sticky.
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 32;
  };

  interface SendVars {
    startNewThread: boolean;
    attachments: MessageAttachmentDto[];
  }
  const send = useMutation({
    mutationFn: ({ startNewThread, attachments }: SendVars) =>
      messagesApi.send({
        recipientUserId,
        body: body.trim(),
        attachments: attachments.map<AttachmentRef>((a) => ({
          resourceType: a.resourceType,
          resourceId: a.resourceId,
        })),
        startNewThread,
      }),
    onSuccess: (msg, vars) => {
      setBody("");
      setPendingAttachments([]);
      stickToBottomRef.current = true;
      onMessageSent?.(msg);
      queryClient.invalidateQueries({ queryKey: ["messaging", "unread"] });
      queryClient.invalidateQueries({ queryKey: ["messaging", "conversations"] });
      // Refresh every entity's Messages tab that this send touched, so the
      // Related Conversations panel reflects the new thread immediately.
      for (const att of vars.attachments) {
        queryClient.invalidateQueries({
          queryKey: messagingKeys.related(att.resourceType, att.resourceId),
        });
      }
      // For the /messages/new path, redirect into the canonical URL once the
      // conversation has an id. The thread state we just sent is preserved
      // because ExistingThread's initial fetch will re-include it.
      if (!conversationId) {
        navigate(`/messages/${msg.conversationId}`, { replace: true });
      }
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiError && err.errors[0]
        ? err.errors[0]
        : "Could not send message.";
      toast({ tone: "error", message: msg });
    },
  });

  const runSend = (opts: { startNewThread?: boolean } = {}) => {
    setForeignContexts(null);
    send.mutate({
      startNewThread: opts.startNewThread ?? false,
      attachments: pendingAttachments,
    });
  };

  // FIND-06 — before the first send of a record-tagged draft, check whether
  // the recipient already has a 1:1 thread about a *different* record. If so,
  // warn before folding this message into it. Existing threads are already
  // on screen, and drafts with no record chip carry nothing to misplace, so
  // both short-circuit straight to send. The check is advisory: any failure
  // falls through to a normal send rather than blocking the user.
  const requestSend = async () => {
    if (!canSubmit) return;
    if (conversationId !== null || pendingAttachments.length === 0) {
      runSend();
      return;
    }
    setCheckingContext(true);
    try {
      const peer = await messagesApi.peerContext(recipientUserId);
      const draftKeys = new Set(
        pendingAttachments.map((a) => `${a.resourceType}:${a.resourceId}`),
      );
      const foreign = peer.conversationId
        ? peer.contexts.filter(
            (c) => !draftKeys.has(`${c.resourceType}:${c.resourceId}`),
          )
        : [];
      if (foreign.length > 0) {
        setForeignContexts(foreign);
      } else {
        runSend();
      }
    } catch {
      runSend();
    } finally {
      setCheckingContext(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (canSubmit) void requestSend();
    }
  };

  const canSubmit =
    canSend && body.trim().length > 0 && !send.isPending && !checkingContext;

  return (
    <>
      <div className="flex items-center gap-space-2 mb-space-3">
        <Button variant="tertiary" size="sm" onClick={() => navigate("/messages")}>
          <ArrowLeft size={16} strokeWidth={1.5} aria-hidden /> Inbox
        </Button>
      </div>

      <div className="bg-white-pure border border-border-light rounded-lg flex flex-col min-h-[60vh]">
        <div className="border-b border-border-light px-space-4 py-space-3">{header}</div>

        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto px-space-4 py-space-3 max-h-[60vh]"
        >
          {messages.length === 0 ? (
            <div className="text-small text-gray-mid text-center py-space-5">
              No messages yet — say hi.
            </div>
          ) : (
            groupedByDay.map((g) => (
              <div key={g.label}>
                <div className="text-label uppercase tracking-label text-gray-mid text-center my-space-3">
                  {g.label}
                </div>
                {g.messages.map((m) => (
                  <MessageBubble key={m.id} message={m} mine={m.senderId === me?.id} tz={tz} />
                ))}
              </div>
            ))
          )}
        </div>

        <div className="border-t border-border-light px-space-3 py-space-3 bg-white-warm">
          {pendingAttachments.length > 0 && (
            <div className="flex flex-wrap gap-space-1 mb-space-2">
              {pendingAttachments.map((a) => (
                <ResourceChip
                  key={`${a.resourceType}-${a.resourceId}`}
                  attachment={a}
                  onRemove={() =>
                    setPendingAttachments((prev) =>
                      prev.filter((p) => !(p.resourceType === a.resourceType && p.resourceId === a.resourceId)))
                  }
                />
              ))}
            </div>
          )}
          <div className="flex items-end gap-space-2">
            <button
              type="button"
              onClick={() => setAttachOpen(true)}
              disabled={!canSend || send.isPending}
              aria-label="Attach a record"
              className={cn(
                "h-10 w-10 inline-flex items-center justify-center rounded-md",
                "text-gold-shadow hover:bg-gold/10 disabled:opacity-50",
              )}
            >
              <Paperclip size={18} strokeWidth={1.5} aria-hidden />
            </button>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={canSend ? "Type a message…" : "Read-only thread."}
              disabled={!canSend || send.isPending}
              rows={1}
              className={cn(
                "flex-1 resize-none rounded-md border border-border-light bg-white-pure",
                "px-space-3 py-space-2 text-small font-body min-h-[40px] max-h-[140px]",
                "focus:outline-none focus:border-gold focus:shadow-goldRing",
              )}
              style={{ minHeight: 40 }}
            />
            <Button
              onClick={() => void requestSend()}
              disabled={!canSubmit}
              loading={send.isPending || checkingContext}
            >
              <Send size={16} strokeWidth={1.5} aria-hidden /> Send
            </Button>
          </div>
        </div>
      </div>

      <AttachResourceModal
        open={attachOpen}
        onClose={() => setAttachOpen(false)}
        onPick={(attachment) => {
          // Optimistic chip — the server will re-resolve the label on send.
          setPendingAttachments((prev) => {
            const exists = prev.some(
              (p) => p.resourceType === attachment.resourceType
                && p.resourceId === attachment.resourceId,
            );
            return exists ? prev : [...prev, attachment];
          });
        }}
      />

      <Modal
        open={!!foreignContexts}
        onClose={() => setForeignContexts(null)}
        title="This conversation is about a different record"
        size="sm"
        footer={
          <>
            <Button variant="secondary" type="button" onClick={() => setForeignContexts(null)}>
              Cancel
            </Button>
            <Button
              variant="secondary"
              type="button"
              loading={send.isPending}
              onClick={() => runSend({ startNewThread: true })}
            >
              Start a new thread
            </Button>
            <Button type="button" loading={send.isPending} onClick={() => runSend()}>
              Send anyway
            </Button>
          </>
        }
      >
        <p className="text-small text-onyx m-0">
          Your existing conversation with this person already references:
        </p>
        <ul className="mt-space-2 mb-space-2 flex flex-wrap gap-space-1 list-none p-0">
          {(foreignContexts ?? []).map((c) => (
            <li key={`${c.resourceType}-${c.resourceId}`}>
              <ResourceChip attachment={c} />
            </li>
          ))}
        </ul>
        <p className="text-small text-gray-mid m-0">
          "Send anyway" adds this message to that thread. "Start a new thread"
          opens a fresh conversation with the same person — pick whichever is
          the right place for it.
        </p>
      </Modal>
    </>
  );
}

function ThreadHeader({ thread }: { thread: ConversationThreadDto }) {
  const profileHref = `/account/profile/${thread.otherUserId}`;
  return (
    <div className="flex items-center gap-space-3">
      <Link to={profileHref} aria-label={`View ${thread.otherUserDisplayName}'s profile`}>
        <Avatar name={thread.otherUserDisplayName} avatarUrl={thread.otherUserAvatarUrl} />
      </Link>
      <div>
        <Link to={profileHref} className="font-display font-bold text-onyx hover:text-gold-shadow">
          {thread.otherUserDisplayName}
        </Link>
        {!thread.viewerIsParticipant && (
          <div className="text-label uppercase tracking-label text-gold-shadow flex items-center gap-space-1">
            <Users size={12} strokeWidth={1.5} aria-hidden />
            Compliance view — read-only
          </div>
        )}
        {thread.viewerIsParticipant && !thread.otherUserIsActive && (
          <div className="text-label text-gray-mid">Account deactivated</div>
        )}
      </div>
    </div>
  );
}

function NewThreadHeader({ displayName }: { displayName: string }) {
  return (
    <div className="flex items-center gap-space-3">
      <Avatar name={displayName} />
      <div className="font-display font-bold text-onyx">{displayName}</div>
    </div>
  );
}

function Avatar({ name, avatarUrl }: { name: string; avatarUrl?: string | null }) {
  if (avatarUrl) {
    return <img src={avatarUrl} alt="" className="h-10 w-10 rounded-pill object-cover" />;
  }
  const initials = name.split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  return (
    <span className="h-10 w-10 rounded-pill bg-gold text-onyx flex items-center justify-center text-small font-display font-bold">
      {initials || "?"}
    </span>
  );
}

function MessageBubble({ message, mine, tz }: { message: MessageDto; mine: boolean; tz: string }) {
  const fullDateTime = formatFullLocalDateTime(message.createdAt, tz);
  return (
    <div className={cn("flex mb-space-2", mine ? "justify-end" : "justify-start")}>
      <div
        className={cn(
          "max-w-[75%] rounded-md px-space-3 py-space-2",
          mine ? "bg-gold text-onyx" : "bg-white-warm text-onyx border border-border-light",
        )}
      >
        {!mine && (
          <div className="text-label font-display font-semibold text-onyx mb-[2px]">
            {message.senderDisplayName}
          </div>
        )}
        <div className="text-small whitespace-pre-wrap break-words">{message.body}</div>
        {message.attachments.length > 0 && (
          <div className="mt-space-2 flex flex-wrap gap-space-1">
            {message.attachments.map((a) => (
              <ResourceChip key={`${a.resourceType}-${a.resourceId}`} attachment={a} />
            ))}
          </div>
        )}
        <div
          className={cn("text-label mt-space-1", mine ? "text-onyx/70" : "text-gray-mid")}
          title={fullDateTime}
        >
          {formatMessageTime(message.createdAt, tz)}
        </div>
      </div>
    </div>
  );
}

function mergeMessages(prev: MessageDto[], fresh: MessageDto[]): MessageDto[] {
  if (fresh.length === 0) return prev;
  const seen = new Set(prev.map((m) => m.id));
  const additions = fresh.filter((m) => !seen.has(m.id));
  if (additions.length === 0) return prev;
  return [...prev, ...additions].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );
}

function groupMessagesByDay(messages: MessageDto[], tz: string): Array<{ label: string; messages: MessageDto[] }> {
  if (messages.length === 0) return [];
  const now = new Date();
  const groups: Array<{ label: string; messages: MessageDto[] }> = [];
  for (const m of messages) {
    // formatDateSeparator computes Today/Yesterday/etc. against the user's
    // LOCAL date — what they see on the wall clock — not UTC.
    const label = formatDateSeparator(m.createdAt, tz, now);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.messages.push(m);
    else groups.push({ label, messages: [m] });
  }
  return groups;
}
